import { Alert, Flex } from '@mantine/core'
import { IconLock } from '@tabler/icons-react'
import { useAuth } from 'context/AuthProvider'
import { useIsAdmin } from 'hooks/auth'
import { useAppTranslation } from 'hooks/useTranslation'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

/**
 * Layout route guarding the imported-application screens.
 *
 * Layers on top of AuthRoute rather than replacing it: AuthRoute establishes
 * that somebody is signed in, this establishes that the somebody is an
 * administrator. Event, venue and organizer routes keep the looser guard
 * because organizer accounts legitimately use them.
 *
 * The decision is three-valued and collapses closed. Only a definite `true`
 * renders the route; a definite `false`, a rejected claim query and an answer
 * that has not arrived yet are all refusals. Rendering the outlet while the
 * answer is pending would leave the boundary open for exactly as long as one
 * round trip takes, which is long enough to read a page.
 *
 * This is defence in depth, not the boundary of record: the Phase 18 RLS
 * policy evaluates the same server-side predicate and returns no rows to the
 * same account. What this stops is a non-administrator seeing the shell of a
 * page whose contents the database would refuse them.
 */
const AdminRoute = () => {
  const { auth } = useAuth()
  const { isAdmin, isLoading } = useIsAdmin()
  const location = useLocation()
  const { t } = useAppTranslation()

  // Signed out: hand the requested location to Login, which validates it
  // through safeReturnPath before navigating. The query string travels with
  // the path because the browser extension's redirect carries `?processed=1`
  // and dropping it would silently change where the curator lands.
  if (!auth) {
    return (
      <Navigate
        to='/login'
        replace
        state={{ path: location.pathname + location.search }}
      />
    )
  }

  // Claim in flight. Render nothing at all — not the outlet, not a redirect to
  // a permissive page, not a spinner a later refactor could mistake for a
  // loading screen it is safe to skip past.
  if (isLoading) {
    return null
  }

  // Anything that is not a definite yes is a no. Deny in place instead of
  // redirecting: a redirect makes a permissions problem look like a broken
  // link, and a curator with two accounts needs to see which one they are on.
  // Nothing identifying and no failure detail is rendered — it would tell an
  // attacker which axis to attack and a legitimate user nothing actionable.
  if (isAdmin !== true) {
    return (
      <Flex p='md'>
        <Alert
          color='red'
          icon={<IconLock size={18} />}
          title={t('applications.access_denied_title')}
        >
          {t('applications.access_denied_body')}
        </Alert>
      </Flex>
    )
  }

  return <Outlet />
}

export default AdminRoute
